<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'

const { t, currentStep, totalSteps, isLoading, initialized, wizardLoadError, canProceed, stepLabels, nextStep, prevStep, focusWizard, initWizard, retryWizardLoad, isLoggedIn, localePath, estimatedPrice, formatPrice } = useTradeInWizard()

async function handleNextStep() {
  await nextStep()
  await focusWizard()
}

async function handlePreviousStep() {
  await prevStep()
  await focusWizard()
}

definePageMeta({
  layout: 'default'
})

useSeoMeta({
  title: () => t('seo.tradeIn.title'),
  description: () => t('seo.tradeIn.description'),
})

useSeoCanonical('/compramos')

// Process steps for How It Works section
const processSteps = computed(() => [
  { number: 1, titleKey: 'sellPage.process.step1.title', descKey: 'sellPage.process.step1.desc', icon: 'i-lucide-smartphone' },
  { number: 2, titleKey: 'sellPage.process.step2.title', descKey: 'sellPage.process.step2.desc', icon: 'i-lucide-badge-euro' },
  { number: 3, titleKey: 'sellPage.process.step3.title', descKey: 'sellPage.process.step3.desc', icon: 'i-lucide-package' },
  { number: 4, titleKey: 'sellPage.process.step4.title', descKey: 'sellPage.process.step4.desc', icon: 'i-lucide-wallet' },
])

// FAQ items
const faqItems = computed(() => [
  { questionKey: 'sellPage.faq.q1.question', answerKey: 'sellPage.faq.q1.answer' },
  { questionKey: 'sellPage.faq.q2.question', answerKey: 'sellPage.faq.q2.answer' },
  { questionKey: 'sellPage.faq.q3.question', answerKey: 'sellPage.faq.q3.answer' },
  { questionKey: 'sellPage.faq.q4.question', answerKey: 'sellPage.faq.q4.answer' },
])

const openFaq = ref<number | null>(null)
const toggleFaq = (index: number) => {
  openFaq.value = openFaq.value === index ? null : index
}

const sellTrustBadges = computed(() => [
  { icon: 'i-lucide-clock-3', label: t('sellPage.trustBadges.fastPayment') },
  { icon: 'i-lucide-truck', label: t('sellPage.trustBadges.freeShipping') },
  { icon: 'i-lucide-shield-check', label: t('sellPage.trustBadges.securePayment') },
])

onMounted(() => {
  initWizard()
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <DSContainer class="pt-5">
      <PageHeroBanner
        theme="sell"
        variant="service"
        :home-label="t('sellPage.breadcrumb.home')"
        :current-label="t('sellPage.breadcrumb.sell')"
        :home-to="localePath('/')"
        :badge="t('sellPage.badge')"
        :title="t('sellPage.title')"
        :title-highlight="t('sellPage.titleHighlight')"
        :subtitle="t('sellPage.subtitle')"
        hero-icon="i-lucide-hand-coins"
        visual-src="/images/heroes/trade-in-box.webp"
        :cta-label="t('sellPage.calculator.cta')"
        cta-to="#trade-in-wizard"
        :trust-badges="sellTrustBadges"
      />
    </DSContainer>

    <DSContainer class="py-12">
      <!-- Trade-In Wizard -->
      <div id="trade-in-wizard" class="w-full mb-16">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800">
          <!-- Progress Bar -->
          <div
            v-if="currentStep <= totalSteps"
            id="trade-in-wizard-progress"
            tabindex="-1"
            class="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-b border-gray-100 dark:border-gray-800 focus:outline-none"
          >
            <div class="flex items-center justify-between mb-2">
              <h2 class="font-semibold text-gray-900 dark:text-white">
                {{ t('sellPage.calculator.title') }}
              </h2>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                {{ t('sellPage.wizard.stepOf', { current: currentStep, total: totalSteps }) }}
              </span>
            </div>
            <div
              class="flex gap-2"
              role="progressbar"
              :aria-label="t('sellPage.calculator.title')"
              :aria-valuemin="1"
              :aria-valuemax="totalSteps"
              :aria-valuenow="currentStep"
              :aria-valuetext="`${t('sellPage.wizard.stepOf', { current: currentStep, total: totalSteps })}: ${stepLabels[currentStep - 1]}`"
            >
              <div
                v-for="step in totalSteps"
                :key="step"
                aria-hidden="true"
                class="flex-1 h-2 rounded-full transition-colors"
                :class="step <= currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'"
              />
            </div>
            <span class="sr-only" aria-live="polite" aria-atomic="true">
              {{ t('sellPage.wizard.stepOf', { current: currentStep, total: totalSteps }) }}: {{ stepLabels[currentStep - 1] }}
            </span>
          </div>

          <div class="p-6 sm:p-8">
            <div v-if="wizardLoadError && !isLoading" role="alert" class="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <p>{{ wizardLoadError }}</p>
              <button type="button" class="mt-3 min-h-11 font-semibold underline" @click="retryWizardLoad">
                {{ t('common.retry') }}
              </button>
            </div>
            <!-- Loading Overlay (shown during init or data loading) -->
            <div v-if="isLoading || !initialized" role="status" aria-live="polite" class="flex items-center justify-center py-16">
              <div aria-hidden="true" class="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
              <span class="sr-only">{{ t('common.loading') }}</span>
            </div>

            <!-- Child Step Pages -->
            <NuxtPage v-else />
          </div>

          <!-- Combined sticky bottom: step-2 estimated price + nav buttons.
               Stacked as one sticky container so they don't overlap on narrow viewports. -->
          <div v-if="currentStep <= totalSteps && initialized && !isLoading" class="sticky bottom-0 z-10">
            <!-- Step 2 estimated price summary -->
            <div v-if="currentStep === 2" class="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 sm:px-8 py-3 flex items-center justify-between shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
              <span class="text-sm text-gray-500 dark:text-gray-400">{{ t('sellPage.wizard.estimate') }}</span>
              <span class="text-xl font-bold text-green-600 dark:text-green-400">{{ formatPrice(estimatedPrice) }}</span>
            </div>

            <!-- Nav buttons -->
            <div class="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 px-6 sm:px-8 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4 rounded-b-2xl flex items-center justify-between" :class="{ 'shadow-[0_-8px_30px_rgba(0,0,0,0.12)]': currentStep !== 2 }">
              <button
                v-if="currentStep > 1"
                class="-ml-3 flex min-h-11 items-center gap-2 rounded-lg px-3 text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
                @click="handlePreviousStep"
              >
                <UIcon name="i-lucide-arrow-left" class="w-5 h-5" />
                {{ t('sellPage.wizard.back') }}
              </button>
              <div v-else />

              <button
                v-if="currentStep < totalSteps"
                :disabled="!canProceed && !(currentStep === 4 && !isLoggedIn)"
                class="flex min-h-11 items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:focus-visible:ring-offset-gray-900"
                @click="handleNextStep"
              >
                {{ currentStep === 4 && !isLoggedIn ? t('sellPage.shipping.loginToContinue') : currentStep === 3 ? t('sellPage.quote.acceptQuote') : t('sellPage.wizard.continue') }}
                <UIcon name="i-lucide-arrow-right" class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <!-- Trust indicators -->
        <div class="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-users" class="w-4 h-4 text-green-600" />
            <span>{{ t('sellPage.calculator.stats.customers') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-star" class="w-4 h-4 text-green-600" />
            <span>{{ t('sellPage.calculator.stats.rating') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-euro" class="w-4 h-4 text-green-600" />
            <span>{{ t('sellPage.calculator.stats.avgPayout') }}</span>
          </div>
        </div>
      </div>

      <!-- How It Works Section -->
      <div class="mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {{ t('sellPage.howItWorks.title') }}
          </h2>
          <p class="text-gray-600 dark:text-gray-400">
            {{ t('sellPage.howItWorks.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <div v-for="(step, index) in processSteps" :key="step.number" class="relative">
            <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-100 dark:border-gray-800 h-full flex flex-col">
              <div class="flex items-center justify-between mb-4">
                <div class="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center font-bold text-white text-sm">{{ step.number }}</div>
                <UIcon v-if="index < processSteps.length - 1" name="i-lucide-arrow-right" class="w-5 h-5 hidden lg:block text-green-600" />
              </div>
              <div class="w-14 h-14 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <UIcon :name="step.icon" class="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <h3 class="font-bold text-gray-900 dark:text-white mb-2 text-lg">{{ t(step.titleKey) }}</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 flex-grow">{{ t(step.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Why Sell With Us Section -->
      <div class="mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ t('sellPage.whySell.title') }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-banknote" class="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('sellPage.whySell.benefit1.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('sellPage.whySell.benefit1.desc') }}</p>
          </div>
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-truck" class="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('sellPage.whySell.benefit2.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('sellPage.whySell.benefit2.desc') }}</p>
          </div>
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-leaf" class="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('sellPage.whySell.benefit3.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('sellPage.whySell.benefit3.desc') }}</p>
          </div>
        </div>
      </div>

      <!-- FAQ Section -->
      <div class="max-w-3xl mx-auto mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ t('sellPage.faq.title') }}</h2>
        </div>
        <div class="space-y-3">
          <div v-for="(item, index) in faqItems" :key="index" class="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <button class="w-full px-6 py-4 flex items-center justify-between text-left" @click="toggleFaq(index)">
              <span class="font-semibold text-gray-900 dark:text-white">{{ t(item.questionKey) }}</span>
              <UIcon :name="openFaq === index ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="w-5 h-5 text-gray-500 flex-shrink-0" />
            </button>
            <Transition name="accordion">
              <div v-if="openFaq === index" class="px-6 pb-4">
                <p class="text-gray-600 dark:text-gray-400 text-sm">{{ t(item.answerKey) }}</p>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Final CTA -->
      <div class="bg-gradient-to-br from-green-600 to-emerald-700 dark:from-green-800 dark:to-emerald-900 rounded-2xl p-8 sm:p-12 text-center">
        <h2 class="text-2xl sm:text-3xl font-bold text-white mb-4">{{ t('sellPage.finalCta.title') }}</h2>
        <p class="text-green-100 mb-6 max-w-2xl mx-auto">{{ t('sellPage.finalCta.subtitle') }}</p>
        <NuxtLink
          :to="localePath('/compramos')"
          class="inline-flex items-center gap-2 bg-white text-green-700 hover:bg-green-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors"
        >
          {{ t('sellPage.finalCta.cta') }}
          <UIcon name="i-lucide-arrow-up" class="w-5 h-5" />
        </NuxtLink>
      </div>
    </DSContainer>
  </div>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 200px;
}
</style>
